/**
 * Serviço otimizado de cache de permissões
 * Implementa estratégia JWT + Cache para performance
 */

import { buildLoginApiUrl } from '../../lib/config';

interface CachedPermissions {
  permissions: any;
  timestamp: number;
  version: number;
}

interface JWTPermissions {
  modules: string[];
  operations: string[];
  compact?: string;
  version?: number;
  isSuperAdmin: boolean;
}

export class PermissionCacheService {
  private static instance: PermissionCacheService;
  private cache = new Map<string, CachedPermissions>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos
  private readonly STORAGE_KEY = 'user_permissions_cache';
  private refreshPromise: Promise<any> | null = null;

  static getInstance(): PermissionCacheService {
    if (!PermissionCacheService.instance) {
      PermissionCacheService.instance = new PermissionCacheService();
    }
    return PermissionCacheService.instance;
  }

  constructor() {
    this.loadFromLocalStorage();
  }

  /**
   * Extrai permissões básicas do JWT token
   */
  getJWTPermissions(): JWTPermissions | null {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;

      const payload = this.decodeJWT(token);
      if (!payload) return null;

      // Extrair permissões do JWT
      return {
        modules: payload.modules || [],
        operations: payload.operations || [],
        compact: payload.permissionsCompact,
        version: payload.permissionsVersion,
        isSuperAdmin: payload.isSuperAdmin || false
      };
    } catch (error) {
      console.warn('Erro ao extrair permissões do JWT:', error);
      return null;
    }
  }

  /**
   * Obtém permissões com estratégia em camadas
   */
  async getPermissions(userId: number): Promise<any> {
    // Layer 1: JWT (imediato - sempre disponível)
    const jwtPermissions = this.getJWTPermissions();
    
    // Se é super admin, não precisa de cache detalhado
    if (jwtPermissions?.isSuperAdmin) {
      return {
        isSuperAdmin: true,
        hasAllPermissions: true,
        modules: ['*'], // Acesso total
        jwtBased: true
      };
    }

    // Layer 2: Cache local (< 1ms)
    const cached = this.getCachedPermissions(userId);
    if (cached && !this.isExpired(userId)) {

      // Se o cache estiver "vazio", buscar imediatamente do backend para evitar menu vazio
      if (this.isEffectivelyEmpty(cached.permissions)) {
        try {
          const fresh = await this.fetchFromAPI(userId);
          this.setCachedPermissions(userId, fresh);
          return {
            ...fresh,
            hasAllPermissions: !!(fresh.isSuperAdmin || fresh.modulePermissions?.['*']?.includes('admin') || fresh.modulePermissions?.['*']?.includes('all') || fresh.modulePermissions?.['*']?.includes('*')),
            fresh: true,
            jwtFallback: jwtPermissions
          };
        } catch (error: any) {
          console.warn('⚠️ Immediate refresh failed, keeping cached permissions:', error?.message || error);
        }
      }

      // Background refresh se necessário
      this.scheduleBackgroundRefresh(userId);
      
      return {
        ...cached.permissions,
        // garantir flags derivadas para compatibilidade
        hasAllPermissions: !!(cached.permissions.isSuperAdmin || cached.permissions.modulePermissions?.['*']?.includes('admin') || cached.permissions.modulePermissions?.['*']?.includes('all') || cached.permissions.modulePermissions?.['*']?.includes('*')),
        cached: true,
        jwtFallback: jwtPermissions
      };
    }
    
    try {
      const fresh = await this.fetchFromAPI(userId);
      this.setCachedPermissions(userId, fresh);
      
      return {
        ...fresh,
        hasAllPermissions: !!(fresh.isSuperAdmin || fresh.modulePermissions?.['*']?.includes('admin') || fresh.modulePermissions?.['*']?.includes('all') || fresh.modulePermissions?.['*']?.includes('*')),
        fresh: true,
        jwtFallback: jwtPermissions
      };
    } catch (error) {
      console.warn('⚠️ API failed, using JWT fallback:', error);
      
      // Fallback para JWT se API falhar
      return {
        ...this.createFallbackPermissions(jwtPermissions),
        hasAllPermissions: !!jwtPermissions?.isSuperAdmin,
        fallback: true,
        error: error.message
      };
    }
  }

  /**
   * Verifica se pode realizar operação em módulo
   */
  canAccess(permissions: any, module: string, operation: string = 'read'): boolean {
    // Super admin sempre pode
    if (permissions.isSuperAdmin || permissions.hasAllPermissions) {
      return true;
    }

    // Verificar permissões específicas
    if (permissions.modulePermissions) {
      const normalizedModule = this.normalizeModuleName(module);
      const normalizedOperation = this.normalizeOperation(operation);

      const modulePerms = permissions.modulePermissions[normalizedModule];
      const globalPerms = permissions.modulePermissions['*'];

      const hasInModule = Array.isArray(modulePerms) && (
        modulePerms.includes(normalizedOperation) ||
        modulePerms.includes('admin') ||
        modulePerms.includes('all') ||
        modulePerms.includes('*')
      );

      const hasInGlobal = Array.isArray(globalPerms) && (
        globalPerms.includes(normalizedOperation) ||
        globalPerms.includes('admin') ||
        globalPerms.includes('all') ||
        globalPerms.includes('*')
      );

      return !!(hasInModule || hasInGlobal);
    }

    // Fallback para JWT compacto
    if (permissions.jwtFallback?.compact) {
      return this.checkCompactPermission(permissions.jwtFallback.compact, module, operation);
    }

    // Fallback para lista de módulos simples (compatibilidade)
    if (permissions.modules && operation === 'read') {
      return permissions.modules.includes(module) || permissions.modules.includes('*');
    }

    return false;
  }

  /**
   * Background refresh sem bloquear UI
   */
  private scheduleBackgroundRefresh(userId: number): void {
    if (this.refreshPromise) return; // Já tem refresh em andamento

    // Só faz refresh se cache está próximo de expirar (80% do TTL)
    const cached = this.getCachedPermissions(userId);
    if (!cached) return;

    const age = Date.now() - cached.timestamp;
    const shouldRefresh = age > (this.CACHE_TTL * 0.8);

    if (shouldRefresh) {
      this.refreshPromise = this.refreshInBackground(userId);
    }
  }

  private async refreshInBackground(userId: number): Promise<void> {
    try {
      const fresh = await this.fetchFromAPI(userId);
      this.setCachedPermissions(userId, fresh);
    } catch (error) {
      console.warn('⚠️ Background refresh failed:', error);
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Força refresh das permissões
   */
  async forceRefresh(userId: number): Promise<any> {
    this.clearCache(userId);
    return this.getPermissions(userId);
  }

  /**
   * Limpa cache do usuário
   */
  clearCache(userId: number): void {
    this.cache.delete(userId.toString());
    this.saveToLocalStorage();
  }

  /**
   * Limpa todo o cache
   */
  clearAllCache(): void {
    this.cache.clear();
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // === Métodos Privados ===

  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  private getCachedPermissions(userId: number): CachedPermissions | null {
    return this.cache.get(userId.toString()) || null;
  }

  private setCachedPermissions(userId: number, permissions: any): void {
    this.cache.set(userId.toString(), {
      permissions,
      timestamp: Date.now(),
      version: permissions.version || 1
    });
    this.saveToLocalStorage();
  }

  private isExpired(userId: number): boolean {
    const cached = this.getCachedPermissions(userId);
    if (!cached) return true;

    const age = Date.now() - cached.timestamp;
    return age > this.CACHE_TTL;
  }

  private async fetchFromAPI(userId: number): Promise<any> {
    const token = localStorage.getItem('access_token');
    if (!token) throw new Error('Token não encontrado');

    // Endpoint otimizado de permissões (cacheado via Redis no backend)
    const url = buildLoginApiUrl('auth/me/permissions');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const processed = this.processPermissionsResponse(data);
    // Fallback para legado quando resposta vier efetivamente vazia
    if (this.isEffectivelyEmpty(processed)) {
      try {
        const legacy = await this.fetchLegacyFromAPI(userId);
        return legacy;
      } catch {
        // mantém processed vazio se fallback falhar
      }
    }
    return processed;
  }

  private processPermissionsResponse(apiData: any): any {
    // Preferir resposta já agregada do backend
    if (apiData && apiData.modulePermissions) {
      const modulePermissions: Record<string, string[]> = {};
      Object.entries(apiData.modulePermissions as Record<string, string[] | string>)
        .forEach(([moduleName, ops]) => {
          const normalizedModule = this.normalizeModuleName(moduleName);
          const opsArray = Array.isArray(ops) ? ops : String(ops).split(',');
          modulePermissions[normalizedModule] = this.normalizeOperations(opsArray);
        });

      return {
        modulePermissions,
        isSuperAdmin: !!apiData.isSuperAdmin,
        version: apiData.version || Date.now(),
        vinculos: apiData.vinculos || [],
      };
    }

    // Suporte a formato em lista: permissions: [{ modulo, operacao, ativo }]
    if (apiData && Array.isArray(apiData.permissions)) {
      const modulePermissions: Record<string, string[]> = {};
      (apiData.permissions as any[]).forEach((p) => {
        if (!p?.ativo) return;
        const m = this.normalizeModuleName(p.modulo);
        const op = this.normalizeOperation(p.operacao);
        if (!m || !op) return;
        if (!modulePermissions[m]) modulePermissions[m] = [];
        if (!modulePermissions[m].includes(op)) modulePermissions[m].push(op);
      });

      return {
        modulePermissions,
        isSuperAdmin: !!apiData.isSuperAdmin,
        version: apiData.version || Date.now(),
      };
    }

    // Suporte a formato compacto
    if (apiData && (apiData.compact || apiData.permissionsCompact)) {
      const compact = apiData.compact || apiData.permissionsCompact;
      const modulePermissions: Record<string, string[]> = {};
      const permissions = this.parseCompactPermissions(compact);
      permissions.forEach(({ module, operations }) => {
        modulePermissions[this.normalizeModuleName(module)] = this.normalizeOperations(operations);
      });
      return {
        modulePermissions,
        isSuperAdmin: !!apiData.isSuperAdmin,
        version: apiData.version || Date.now(),
      };
    }

    // Fallback simples baseado em lista de módulos
    if (apiData && apiData.modules) {
      const modulePermissions: Record<string, string[]> = {};
      (apiData.modules as string[]).forEach((m) => {
        modulePermissions[this.normalizeModuleName(m)] = ['read'];
      });
      return {
        modulePermissions,
        isSuperAdmin: !!apiData.isSuperAdmin,
        version: apiData.version || Date.now(),
      };
    }

    // Último recurso: manter estrutura vazia
    return {
      modulePermissions: {},
      isSuperAdmin: false,
      version: Date.now(),
    };
  }

  private normalizeOperations(ops: string[]): string[] {
    const mapped = ops
      .map((op) => this.normalizeOperation(op))
      .filter(Boolean) as string[];
    // dedupe
    return Array.from(new Set(mapped));
  }

  private normalizeOperation(op: string): string {
    const value = (op || '').toString().toLowerCase();
    const map: Record<string, string> = {
      // canonical
      read: 'read',
      view: 'view',
      list: 'view',  // list maps to view now
      write: 'write',
      create: 'create',
      update: 'update',
      delete: 'delete',
      admin: 'admin',
      all: 'all',
      '*': '*',
      // uppercase synonyms (fall through via lowercase)
    };
    return map[value] || value;
  }

  private normalizeModuleName(name: string): string {
    const raw = (name || '').toString().toLowerCase();
    // strip non-alphanumerics
    const stripped = raw.replace(/[^a-z0-9]/g, '');
    const map: Record<string, string> = {
      // canonical domain names used across the app (match Layout.tsx and routes)
      turmas: 'turma',
      disciplinas: 'disciplina',
      cursos: 'curso',
      alunos: 'aluno',
      funcionarios: 'funcionario',
      escolas: 'escola',
      secretarias: 'secretaria',
      usuarios: 'user',
      usuario: 'user',
      users: 'user',
      permissoes: 'permissao',
      permissions: 'permissao',
      roles: 'role',
      convites: 'invite',
      invites: 'invite',
      anosletivos: 'anoletivo',
      matrizcurriculares: 'matrizcurricular',
      matrizcurriculo: 'matrizcurricular',
      curriculo: 'matrizcurricular',
      gradehorarias: 'gradehoraria',
      professores: 'professor',
      cargos: 'cargo',
    };
    return map[stripped] || stripped;
  }

  private isEffectivelyEmpty(perms: any): boolean {
    if (!perms) return true;
    if (perms.isSuperAdmin || perms.hasAllPermissions) return false;
    const mp = perms.modulePermissions || {};
    if (typeof mp !== 'object') return true;
    const keys = Object.keys(mp);
    if (keys.length === 0) return true;
    // only wildcard but empty operations array
    if (keys.length === 1 && keys[0] === '*') {
      const ops = mp['*'];
      return !Array.isArray(ops) || ops.length === 0;
    }
    // check if every module has empty ops
    return keys.every((k) => !Array.isArray(mp[k]) || mp[k].length === 0);
  }

  private async fetchLegacyFromAPI(userId: number): Promise<any> {
    const token = localStorage.getItem('access_token');
    if (!token) throw new Error('Token não encontrado');

    const url = buildLoginApiUrl('auth/me');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    // Extrair permissões dos vínculos (legado)
    const modulePermissions: Record<string, string[]> = {};
    if (data?.vinculos) {
      data.vinculos.forEach((v: any) => {
        if (!v?.ativo) return;
        const rolePermissions = v.role?.permissoes || [];
        rolePermissions.forEach((rp: any) => {
          const module = this.normalizeModuleName(rp?.permissao?.modulo);
          const operation = this.normalizeOperation(rp?.permissao?.operacao);
          if (module && operation && rp?.permissao?.ativo) {
            if (!modulePermissions[module]) modulePermissions[module] = [];
            if (!modulePermissions[module].includes(operation)) {
              modulePermissions[module].push(operation);
            }
          }
        });
      });
    }

    return {
      modulePermissions,
      isSuperAdmin: !!data?.isSuperAdmin,
      version: Date.now(),
      vinculos: data?.vinculos || [],
    };
  }

  private createFallbackPermissions(jwtPerms: JWTPermissions | null): any {
    if (!jwtPerms) {
      return {
        modulePermissions: {},
        isSuperAdmin: false,
        fallbackMode: true
      };
    }

    // Converter permissões compactas para formato completo
    const modulePermissions: Record<string, string[]> = {};
    
    if (jwtPerms.compact) {
      const permissions = this.parseCompactPermissions(jwtPerms.compact);
      permissions.forEach(({ module, operations }) => {
        modulePermissions[module] = operations;
      });
    } else {
      // Fallback simples: assume 'read' para todos os módulos listados
      jwtPerms.modules.forEach(module => {
        modulePermissions[module] = ['read'];
      });
    }

    return {
      modulePermissions,
      isSuperAdmin: jwtPerms.isSuperAdmin,
      fallbackMode: true
    };
  }

  private checkCompactPermission(compact: string, module: string, operation: string): boolean {
    const permissions = this.parseCompactPermissions(compact);
    const modulePerms = permissions.find(p => p.module === module);
    return modulePerms ? modulePerms.operations.includes(operation) : false;
  }

  private parseCompactPermissions(compact: string): Array<{ module: string, operations: string[] }> {
    // Formato: "turma:rwa,disciplina:r,aluno:rw"
    return compact.split(',').map(item => {
      const [moduleCode, ops] = item.split(':');
      return {
        module: this.expandModuleName(moduleCode),
        operations: ops.split('').map(op => this.expandOperation(op))
      };
    });
  }

  private expandModuleName(code: string): string {
    const moduleMap: Record<string, string> = {
      't': 'turma',
      'd': 'disciplina', 
      'a': 'aluno',
      'm': 'matricula',
      'c': 'curso',
      'f': 'funcionario',
      's': 'secretaria',
      'e': 'escola'
    };
    return moduleMap[code] || code;
  }

  private expandOperation(code: string): string {
    const operationMap: Record<string, string> = {
      'r': 'read',
      'v': 'view',
      'w': 'write',
      'c': 'create',
      'u': 'update',
      'd': 'delete',
      'a': 'admin'
    };
    return operationMap[code] || code;
  }

  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        Object.entries(data).forEach(([key, value]) => {
          this.cache.set(key, value as CachedPermissions);
        });
      }
    } catch (error) {
      console.warn('Erro ao carregar cache do localStorage:', error);
    }
  }

  private saveToLocalStorage(): void {
    try {
      const data: Record<string, CachedPermissions> = {};
      this.cache.forEach((value, key) => {
        data[key] = value;
      });
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Erro ao salvar cache no localStorage:', error);
    }
  }
}

// Export singleton
export const permissionCache = PermissionCacheService.getInstance();